import { type QueryClient, type QueryKey, useQueries, useQuery, useQueryClient } from "@tanstack/react-query"
import { type ReactNode, createContext, useContext, useEffect, useRef, useState } from "react"
import type { Schemas, SlicePayload } from "@norn/sdk"
import { parseExact, stringifyExact } from "./json"
import {
  type Ux,
  type UxActionBinding,
  type UxDataBinding,
  type UxField,
  type UxForm,
  type UxNavigationItem,
  type UxNode,
  type UxPage,
  type UxPlacement,
  type WidgetDefinitionSet,
  byId,
  objectTypeOf,
} from "./model"
import type { ConditionSources, EvaluatorState, NornEvaluate } from "./conditions"
import { type BindingRequest, type Unavailable, bindingRequest, directDependencies } from "./bindings"
import { message, NoticeError, noticeOf, rendererNotice, type Notice } from "./messages"

export interface WidgetScope {
  readonly orgId: string
  readonly environmentId: string
}

type ObjectRecord = Schemas["ObjectResponse"]

/** What one data binding holds right now. A list's `cursor` means more may exist, whatever its length. */
export type BindingState = (
  | { readonly status: "loading" }
  | { readonly status: "list"; readonly objectType: string; readonly rows: readonly ObjectRecord[]; readonly duplicateKeys?: ReadonlySet<string>; readonly cursor: string | null; readonly stalled: boolean; readonly loadingMore: boolean; readonly moreError: unknown; readonly continues: boolean }
  | { readonly status: "detail"; readonly objectType: string; readonly record: ObjectRecord | null }
  | { readonly status: "error"; readonly error: unknown }
  | { readonly status: "unavailable"; readonly why: Unavailable }
  /** A binding that reads a list's current record, before that list has a selection. */
  | { readonly status: "awaiting_selection"; readonly sourceBindingId: string }
) & {
  /** The cache head observed by this state, including any continuation tied to that head. */
  readonly readHead?: object
  /** Exact request identity, retained during failures so recovery targets this view's reads. */
  readonly readKey?: QueryKey
}

export interface WidgetRefusal {
  readonly message: string
  readonly explanation: string
  readonly code: string
  readonly category: string
}

/** A typed refusal preserved from an MCP tool result or the Admin SDK. */
export class WidgetHostError extends NoticeError {
  constructor(
    notice: string | Notice,
    readonly refusal?: WidgetRefusal,
    readonly status?: number,
    readonly isError = false,
    readonly structuredContent?: unknown,
  ) {
    super(typeof notice === "string" ? { text: notice } : notice, (key, values) => message("en", key, values))
    this.name = "WidgetHostError"
  }
}

export interface WidgetActionCall {
  readonly bindingId: string
  readonly actionId: string
  readonly input: Readonly<Record<string, unknown>>
  readonly actor: string
}

export interface WidgetPrincipal {
  readonly actor: string
  readonly personKey: string | null
}

/** One admitted binding read: page context and current-record keys in the endpoint's query form. */
export interface WidgetBindingRead {
  readonly bindingId: string
  readonly kind: UxDataBinding["query"]["kind"]
  /** `context/<page>/<param>` and `record.<binding>` parameters (see `bindingRequest`). */
  readonly query: Readonly<Record<string, string>>
  /** The page context the query was built from, for hosts that pass it whole. */
  readonly context: Readonly<Record<string, unknown>>
  readonly cursor: string | null
}

/** The only host effects the shared renderer can request. */
export interface WidgetHost {
  /** Isolates query cache entries for separate host sessions. */
  readonly cacheKey: string
  /** Execute one admitted binding (#3211); resolves to the cell's kind-tagged `BindingResponse` JSON text. */
  readBinding(request: WidgetBindingRead, signal?: AbortSignal): Promise<string>
  /** SDK-verified authorized head projections; invalidation clears every retained read. */
  subscribeBinding?(request: WidgetBindingRead, observer: WidgetBindingObserver): () => void
  /** Bindings this host cannot execute at all, reported before any read. */
  unsupported?(binding: UxDataBinding): Unavailable | undefined
  /** Whether this host can continue a list from a cursor. */
  readonly continues?: boolean
  action(call: WidgetActionCall): Promise<void>
  principal(): Promise<WidgetPrincipal>
  navigate?(pageId: string, params: Record<string, string>): void
  explain?(objectType: string, key: string): void
  /** Open a runtime identity in the host's object investigation surface. */
  openGraph?(objectType: string, key: string): void
  /** The host's own wording for a failure it raised (unreachable cell, transport), in the current language. */
  describeError?(error: unknown): string | undefined
}

export interface WidgetBindingObserver {
  readonly onResult: (result: SlicePayload) => void
  readonly onInvalidated: () => void
  readonly onError: (error: unknown) => void
}

type BindingResponse = Schemas["BindingResponse"]
type Page = { readonly rows: readonly ObjectRecord[]; readonly cursor: string | null; readonly stalled: boolean }
/**
 * Pages read past the first, tied to the first page they continue. `pages` is the window the reader
 * loaded (one per "load more"); a refresh re-reads exactly that many pages, even when they came back empty.
 */
type Continuation = { readonly request: string; readonly after: FirstPage | undefined; readonly page: Page; readonly pages: number; readonly loading: boolean; readonly error?: unknown }
type FirstPage = { readonly kind: "loading" } | { readonly kind: "error"; readonly error: unknown } | { readonly kind: "detail"; readonly record: ObjectRecord | null } | { readonly kind: "list"; readonly page: Page }

/** `signal`, also aborted after `ms` (AbortSignal.any is missing on some native runtimes). */
function withTimeout(signal: AbortSignal, ms: number): AbortSignal {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), ms)
  const stop = () => { clearTimeout(timer); controller.abort() }
  if (signal.aborted) stop()
  else signal.addEventListener("abort", stop, { once: true })
  return controller.signal
}

/** Empty pages the renderer follows on its own before asking the operator to continue. */
export const EMPTY_PAGE_HOPS = 3

/**
 * How often retained continuation pages (rows past the live head) are read again. The live head
 * covers only the first page; without this, a change confined to a later page stays stale while the
 * head is unchanged.
 */
export const CONTINUATION_REFRESH_MS = 30_000

/**
 * Read every binding through the host's admitted binding read, which executes filters, sorting, paging
 * and traversal on the cell (#3211). Bindings that read another list's current record wait for its
 * single selection, checked against the rows the cell returned for that list. Lists follow cursors: a
 * short or empty page is never the end, and a continuation that makes no progress stops as "still
 * scanning" instead of looping.
 */
export function useBindings(
  scope: WidgetScope,
  appId: string,
  set: WidgetDefinitionSet,
  page: UxPage,
  ux: Ux,
  bindings: readonly UxDataBinding[],
  context: Readonly<Record<string, unknown>>,
  host: WidgetHost,
  selection: { readonly selectable: ReadonlySet<string>; readonly selected: ReadonlyMap<string, readonly string[]> } = { selectable: new Set(), selected: new Map() },
  locale = "en",
): { readonly states: Map<string, BindingState>; readonly loadMore: (bindingId: string) => void } {
  const queryClient = useQueryClient()
  const all = byId(ux.dataBindings, (b) => b.bindingId)
  const base = ["widget-binding", host.cacheKey, scope.orgId, scope.environmentId] as const
  // A binding's cache identity: the app, the admitted binding itself and the definitions revision,
  // so two widgets that reuse a binding id never share rows.
  const keyOf = (b: UxDataBinding, req: BindingRequest) => [...base, appId, set.version ?? null, b.bindingId, b, req] as const
  // Pages read past the first, per binding and request, tied to the first page they continue: when
  // that page is read again (an action, a refresh), they are dropped and continuation starts over.
  const [extra, setExtra] = useState<ReadonlyMap<string, Continuation>>(new Map())
  const firstRead = (b: UxDataBinding, req: BindingRequest) => queryClient.getQueryData<FirstPage>(keyOf(b, req))
  const current = (b: UxDataBinding, req: BindingRequest, readAt = firstRead(b, req)) => {
    const more = extra.get(b.bindingId)
    return more && req.kind === "ready" && more.request === stringifyExact(keyOf(b, req)) && more.after === readAt ? more : undefined
  }

  // Requests are built sources first, so a list's current record is checked against the rows the cell
  // returned for it: a selection whose row has left the list (say, after an approval) is no selection.
  const built = new Map<string, BindingRequest>()
  const rowsOf = (id: string): readonly ObjectRecord[] | undefined => {
    const req = built.get(id)
    const b = all.get(id)
    if (req?.kind !== "ready" || !b) return undefined
    const first = queryClient.getQueryData<FirstPage>(keyOf(b, req))
    if (first?.kind !== "list") return undefined
    const more = current(b, req)
    return more ? [...first.page.rows, ...more.page.rows] : first.page.rows
  }
  const currentKey = (id: string) => {
    const keys = selection.selected.get(id)
    const key = selection.selectable.has(id) && keys?.length === 1 ? keys[0] : undefined
    return key !== undefined && rowsOf(id)?.some((r) => r.key === key) ? key : undefined
  }
  let pending = [...bindings]
  for (let pass = 0; pending.length > 0 && pass <= bindings.length; pass++) {
    pending = pending.filter((b) => {
      if (directDependencies(b).some((d) => d !== b.bindingId && pending.some((p) => p.bindingId === d))) return true
      built.set(b.bindingId, bindingRequest(b, all, page, context, currentKey, locale))
      return false
    })
  }
  for (const b of pending) built.set(b.bindingId, bindingRequest(b, all, page, context, currentKey, locale))
  for (const b of bindings) {
    const why = host.unsupported?.(b)
    if (why) built.set(b.bindingId, { kind: "unavailable", why })
  }
  const requests = bindings.map((b) => [b, built.get(b.bindingId) as BindingRequest] as const)

  const liveRequests = host.subscribeBinding ? stringifyExact(requests.flatMap(([binding, req]) => req.kind === "ready" ? [{
    key: keyOf(binding, req),
    request: { bindingId: binding.bindingId, kind: binding.query.kind, query: req.query, context, cursor: null },
  }] : [])) : "[]"
  useLiveBindings(queryClient, host, liveRequests)

  const results = useQueries({
    queries: requests.map(([b, req]) => ({
      queryKey: keyOf(b, req),
      enabled: req.kind === "ready" && (!host.subscribeBinding || liveEntries.get(queryClient)?.get(stringifyExact(keyOf(b, req)))?.ready === true),
      // Every fresh head drops continuation pages even when its rows are unchanged.
      structuralSharing: false,
      queryFn: ({ signal }: { signal: AbortSignal }) => firstPage(b, req.kind === "ready" ? req.query : {}, signal),
    })),
  })

  const states = new Map<string, BindingState>()
  requests.forEach(([b, req], i) => {
    const r = results[i]
    const objectType = objectTypeOf(set, b.object) ?? b.object.id
    if (req.kind === "unavailable") states.set(b.bindingId, { status: "unavailable", why: req.why })
    else if (req.kind === "awaiting_selection") states.set(b.bindingId, { status: "awaiting_selection", sourceBindingId: req.sourceBindingId })
    else if (!r || r.isPending) states.set(b.bindingId, { status: "loading" })
    else if (r.isError) states.set(b.bindingId, { status: "error", error: r.error })
    else if (r.data.kind === "loading") states.set(b.bindingId, { status: "loading" })
    else if (r.data.kind === "error") states.set(b.bindingId, { status: "error", error: r.data.error })
    else if (r.data.kind === "detail") states.set(b.bindingId, { status: "detail", objectType, record: r.data.record, readHead: r.data })
    else {
      // Read from the observed result so a re-read with identical rows still re-renders and drops stale pages.
      const more = current(b, req, r.data)
      const combinedRows = more ? [...r.data.page.rows, ...more.page.rows] : r.data.page.rows
      const duplicateKeys = duplicates(combinedRows)
      const rows = more ? dedupe(combinedRows) : combinedRows
      const tail = more?.page ?? r.data.page
      states.set(b.bindingId, { status: "list", objectType, rows, duplicateKeys, cursor: tail.cursor, stalled: tail.stalled, loadingMore: more?.loading ?? false, moreError: more?.error, continues: host.continues !== false, readHead: r.data })
    }
    const state = states.get(b.bindingId)
    if (state) states.set(b.bindingId, { ...state, readKey: keyOf(b, req) })
  })

  // Retained continuation pages, re-read on a bound: from the current head's cursor, the same number
  // of pages the reader loaded, replaced in place only while that head and that continuation still
  // stand. One refresh per binding at a time; unmounting aborts them, and a refresh stops between
  // reads once its head or continuation has changed.
  const latest = useRef({ extra, requests, firstRead, follow, keyOf })
  latest.current = { extra, requests, firstRead, follow, keyOf }
  useEffect(() => {
    const running = new Map<string, AbortController>()
    const timer = setInterval(() => {
      const { extra: retained, requests: now, firstRead: head, keyOf: keyNow } = latest.current
      for (const [bindingId, more] of retained) {
        const entry = now.find(([b]) => b.bindingId === bindingId)
        if (running.has(bindingId) || more.loading || more.pages === 0 || !entry || entry[1].kind !== "ready") continue
        const [binding, req] = entry
        const after = head(binding, req)
        if (after !== more.after || after?.kind !== "list" || after.page.cursor === null || stringifyExact(keyNow(binding, req)) !== more.request) continue
        const controller = new AbortController()
        running.set(bindingId, controller)
        const stands = () => !controller.signal.aborted && latest.current.extra.get(bindingId) === more && latest.current.firstRead(binding, req) === after
        void (async () => {
          let cursor: string | null = after.page.cursor
          let rows: ObjectRecord[] = []
          let stalled = false
          for (let page = 0; cursor !== null && page < more.pages && stands(); page++) {
            const next = await latest.current.follow(binding, req.query, cursor, withTimeout(controller.signal, 60_000))
            rows = [...rows, ...next.rows]
            cursor = next.cursor
            stalled = next.stalled
            if (stalled) break
          }
          if (stands()) setExtra((m) => (m.get(bindingId) === more ? new Map(m).set(bindingId, { ...more, page: { rows, cursor, stalled } }) : m))
        })()
          // A failed or abandoned re-read keeps the retained rows as they were; the next tick reads them again.
          .catch(() => undefined)
          .finally(() => running.delete(bindingId))
      }
    }, CONTINUATION_REFRESH_MS)
    return () => {
      clearInterval(timer)
      for (const controller of running.values()) controller.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reads the latest render through `latest`
  }, [])

  const loadMore = (bindingId: string) => {
    const entry = requests.find(([b]) => b.bindingId === bindingId)
    const state = states.get(bindingId)
    if (!entry || entry[1].kind !== "ready" || state?.status !== "list" || state.cursor === null || state.loadingMore || !state.continues) return
    const [binding, req] = entry
    const request = stringifyExact(keyOf(binding, req))
    const after = firstRead(binding, req)
    const held = current(binding, req)
    const kept = held?.page ?? { rows: [], cursor: state.cursor, stalled: false }
    const pages = (held?.pages ?? 0) + 1
    setExtra((m) => new Map(m).set(bindingId, { request, after, page: kept, pages: pages - 1, loading: true }))
    // A result that arrives after the first page was read again continues an outdated list: dropped.
    const settle = (entryFor: (m: ReadonlyMap<string, unknown>) => Continuation) =>
      setExtra((m) => {
        const now = m.get(bindingId)
        if (!now || now.request !== request || now.after !== after || firstRead(binding, req) !== after) return m
        return new Map(m).set(bindingId, entryFor(m))
      })
    void follow(binding, req.query, state.cursor, AbortSignal.timeout(60_000)).then(
      (next) => settle(() => ({ request, after, page: { rows: [...kept.rows, ...next.rows], cursor: next.cursor, stalled: next.stalled }, pages, loading: false })),
      (error: unknown) => settle(() => ({ request, after, page: kept, pages: pages - 1, loading: false, error })),
    )
  }
  return { states, loadMore }

  async function follow(binding: UxDataBinding, query: Readonly<Record<string, string>>, cursor: string | null, signal: AbortSignal): Promise<Page> {
    return readListPage(host, { bindingId: binding.bindingId, kind: binding.query.kind, query, context, cursor }, signal)
  }

  async function firstPage(binding: UxDataBinding, query: Readonly<Record<string, string>>, signal: AbortSignal): Promise<FirstPage> {
    return readBindingHead(host, { bindingId: binding.bindingId, kind: binding.query.kind, query, context, cursor: null }, signal)
  }
}

async function readBindingHead(host: WidgetHost, request: WidgetBindingRead, signal: AbortSignal): Promise<FirstPage> {
  if (request.kind === "object_detail" || request.kind === "object_runtime_detail") {
    try {
      const result = parseExact(await host.readBinding(request, signal)) as BindingResponse
      if (result.kind !== "object_detail") throw new WidgetHostError({ key: "host.invalidResult" })
      return { kind: "detail", record: result.item }
    } catch (error) {
      if (error instanceof WidgetHostError && error.status === 404) return { kind: "detail", record: null }
      throw error
    }
  }
  return { kind: "list", page: await readListPage(host, request, signal) }
}

async function readListPage(host: WidgetHost, request: WidgetBindingRead, signal: AbortSignal): Promise<Page> {
  let incoming = request.cursor
  for (let hop = 0; ; hop++) {
    const result = parseExact(await host.readBinding({ ...request, cursor: incoming }, signal)) as BindingResponse
    if (result.kind === "object_detail") throw new WidgetHostError({ key: "binding.answeredAsDetail", values: { bindingId: request.bindingId } })
    const next = result.nextCursor ?? null
    if (result.items.length === 0 && next !== null && next === incoming) return { rows: [], cursor: next, stalled: true }
    if (result.items.length > 0 || next === null || hop >= EMPTY_PAGE_HOPS) return { rows: result.items, cursor: next, stalled: result.items.length === 0 && next !== null }
    incoming = next
  }
}

// Share the existing query cache and one SDK subscription per exact binding request across views.
type LiveBindingEntry = { users: number; ready: boolean; stop: () => void }
const liveEntries = new WeakMap<QueryClient, Map<string, LiveBindingEntry>>()

function useLiveBindings(queryClient: QueryClient, host: WidgetHost, serialized: string): void {
  const retained = useRef(new Map<string, () => void>())
  useEffect(() => {
    const releases = retained.current
    return () => {
      for (const release of releases.values()) release()
      releases.clear()
    }
  }, [queryClient, host])

  useEffect(() => {
    if (!host.subscribeBinding) return
    const requests = parseExact(serialized) as Array<{ key: readonly unknown[]; request: WidgetBindingRead }>
    let entries = liveEntries.get(queryClient)
    if (!entries) { entries = new Map(); liveEntries.set(queryClient, entries) }
    const wanted = new Set(requests.map(({ key }) => stringifyExact(key)))
    // Selection changes replace only dependent requests, preserving their source rows and stream.
    for (const [id, release] of retained.current) {
      if (!wanted.has(id)) { release(); retained.current.delete(id) }
    }
    for (const { key, request } of requests) {
      const id = stringifyExact(key)
      if (retained.current.has(id)) continue
      let entry = entries.get(id)
      if (!entry) {
        queryClient.setQueryData<FirstPage>(key, { kind: "loading" })
        let active = true
        const publish = (value: FirstPage) => { if (active) queryClient.setQueryData(key, value) }
        const live: LiveBindingEntry = { users: 0, ready: false, stop: () => {} }
        const cancel = () => { void queryClient.cancelQueries({ queryKey: key, exact: true }) }
        const stop = host.subscribeBinding!(request, {
          onResult: () => {
            const first = !live.ready
            live.ready = true
            // Keep the existing query lifecycle, including action/focus refetch and cancellation.
            if (first) publish({ kind: "loading" })
            void queryClient.invalidateQueries({ queryKey: key, exact: true })
          },
          onInvalidated: () => { live.ready = false; cancel(); publish({ kind: "loading" }) },
          onError: (error) => { live.ready = false; cancel(); publish({ kind: "error", error }) },
        })
        live.stop = () => { active = false; live.ready = false; cancel(); stop(); queryClient.setQueryData<FirstPage>(key, { kind: "loading" }) }
        entry = live
        entries.set(id, entry)
      }
      entry.users++
      const acquired = entry
      retained.current.set(id, () => {
        if (--acquired.users === 0) { acquired.stop(); entries.delete(id) }
      })
    }
  }, [queryClient, host, serialized])
}

function dedupe(rows: readonly ObjectRecord[]): ObjectRecord[] {
  const byKey = new Map<string, ObjectRecord>()
  for (const r of rows) if (!byKey.has(r.key)) byKey.set(r.key, r)
  return [...byKey.values()]
}

function duplicates(rows: readonly ObjectRecord[]): ReadonlySet<string> {
  const seen = new Set<string>()
  const duplicate = new Set<string>()
  for (const row of rows) {
    if (seen.has(row.key)) duplicate.add(row.key)
    else seen.add(row.key)
  }
  return duplicate
}

export interface OpenForm {
  readonly formId: string
  readonly placementId: string
  /** A fresh token for every open, even when the same placement and record are reused. */
  readonly sessionId: number
  /** The record the form was opened for, with the binding it came from; its key travels with it. */
  readonly record: RuntimeRecord | null
  readonly recordBindingId: string | null
}

export interface PageRuntime {
  readonly scope: WidgetScope
  readonly set: WidgetDefinitionSet
  readonly ux: Ux
  readonly appId: string
  readonly page: UxPage
  readonly nodes: Map<string, UxNode>
  readonly bindings: Map<string, BindingState>
  /** Read a list binding's next page from its cursor. */
  readonly loadMore: (bindingId: string) => void
  readonly context: Readonly<Record<string, unknown>>
  readonly locale: string
  readonly fields: Map<string, UxField>
  readonly forms: Map<string, UxForm>
  readonly placements: Map<string, UxPlacement>
  readonly navigation: Map<string, UxNavigationItem>
  readonly actions: Map<string, UxActionBinding>
  readonly openedForms: Map<string, OpenForm>
  readonly openForm: (form: Omit<OpenForm, "sessionId">) => void
  readonly closeForm: (formId: string) => void
  readonly navigate?: (pageId: string, params: Record<string, string>) => void
  readonly explain?: (objectType: string, key: string) => void
  readonly selections: ReadonlyMap<string, readonly string[]>
  readonly select: (bindingId: string, keys: readonly string[]) => void
  readonly evaluator: EvaluatorState
  readonly principal: PrincipalState
  readonly host: WidgetHost
}

export type PrincipalState = { readonly status: "loading" } | { readonly status: "ready"; readonly actor: string; readonly personKey: string | null } | { readonly status: "failed"; readonly error: unknown }

export function useEvaluator(loadEvaluator: (locale?: string) => Promise<NornEvaluate>, locale = "en"): EvaluatorState {
  const query = useQuery({
    queryKey: ["norn-widget-evaluator"],
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    queryFn: async () => ({ evaluate: await loadEvaluator(locale) }),
  })
  if (query.isPending) return { status: "loading" }
  if (query.isError) return { status: "failed", reason: rendererNotice(noticeOf(query.error), locale) }
  return { status: "ready", evaluate: query.data.evaluate }
}

export function usePrincipal(scope: WidgetScope, host: WidgetHost): PrincipalState {
  const query = useQuery({
    queryKey: ["widget-principal", host.cacheKey, scope.orgId, scope.environmentId],
    staleTime: 60_000,
    retry: false,
    queryFn: () => host.principal(),
  })
  if (query.isPending) return { status: "loading" }
  if (query.isError) return { status: "failed", error: query.error }
  return { status: "ready", actor: query.data.actor, personKey: query.data.personKey }
}

export function buildMaps(ux: Ux) {
  return {
    fields: byId(ux.fields, (field) => field.fieldId),
    forms: byId(ux.forms, (form) => form.formId),
    placements: byId(ux.actionPlacements, (placement) => placement.placementId),
    navigation: byId(ux.navigationItems, (item) => item.itemId),
    actions: byId(ux.actionBindings, (action) => action.bindingId),
  }
}

const RuntimeContext = createContext<PageRuntime | null>(null)
export const RuntimeProvider = RuntimeContext.Provider

export function useRuntime(): PageRuntime {
  const value = useContext(RuntimeContext)
  if (!value) throw new Error("useRuntime outside a WidgetIR page")
  return value
}

const RecordContext = createContext<ReadonlyMap<string, RuntimeRecord | null>>(new Map())

export function RecordScope({ bindingId, record, children }: { bindingId: string; record: RuntimeRecord | null; children: ReactNode }) {
  const parent = useContext(RecordContext)
  const next = new Map(parent)
  next.set(bindingId, record)
  return <RecordContext.Provider value={next}>{children}</RecordContext.Provider>
}

export function useCurrentRecord(bindingId: string): RuntimeRecord | null | undefined {
  return useContext(RecordContext).get(bindingId)
}

export interface RowRecord {
  readonly bindingId: string
  readonly record: RuntimeRecord
}

/** Runtime row versions are canonical decimal strings even while the transport schema is numeric. */
export interface RuntimeRecord {
  readonly masked?: ObjectRecord["masked"]
  readonly key: string
  readonly values: unknown
  readonly version: unknown
}

export function useRecordOf(row?: RowRecord): (bindingId: string) => RuntimeRecord | undefined {
  const runtime = useRuntime()
  const scoped = useContext(RecordContext)
  return (bindingId) => {
    if (row && row.bindingId === bindingId) return row.record
    if (scoped.has(bindingId)) return scoped.get(bindingId) ?? undefined
    const state = runtime.bindings.get(bindingId)
    if (state?.status === "detail" && state.record) return state.record
    if (state?.status !== "list") return undefined
    const single = [...runtime.nodes.values()].some((node) => node.component.kind === "list" && node.component.dataBindingId === bindingId && node.component.selection === "single")
    const keys = runtime.selections.get(bindingId)
    if (!single || keys?.length !== 1 || state.duplicateKeys?.size) return undefined
    return state.rows.find((record) => record.key === keys[0])
  }
}

export function useConditionSources(row?: RowRecord, draft?: ConditionSources["draft"], childRow?: ConditionSources["row"]): ConditionSources {
  const runtime = useRuntime()
  const recordOf = useRecordOf(row)
  return {
    record: (id) => {
      const r = recordOf(id)
      return r ? { values: r.values as Readonly<Record<string, unknown>>, key: r.key } : undefined
    },
    selection: (id) => {
      const state = runtime.bindings.get(id)
      const selectable = [...runtime.nodes.values()].some((node) => node.component.kind === "list" && node.component.dataBindingId === id && node.component.selection !== "none")
      const keys = runtime.selections.get(id) ?? []
      if (!selectable || state?.status !== "list" || state.duplicateKeys?.size) return undefined
      const rows = keys.map((key) => state.rows.find((record) => record.key === key))
      if (rows.some((record) => !record)) return undefined
      return rows.map((record) => record!.values as Readonly<Record<string, unknown>>)
    },
    row: childRow,
    draft,
    page: { pageId: runtime.page.pageId, context: runtime.context },
  }
}

export function presentationFor(fields: Map<string, UxField>, bindingId: string, propertyId: string): UxField | undefined {
  for (const field of fields.values()) {
    if (field.target.kind === "property" && field.target.property.bindingId === bindingId && field.target.property.propertyId === propertyId) return field
  }
  return undefined
}
